import { computed, shallowRef, watch } from 'vue'
import { defineStore } from 'pinia'
import type { TypeRecherche } from '@/features/application/types'
import type { PagedRequest } from '@/features/interface/recherches/storeRechercheBuilder'
import type { FavorisDTO } from '@/features/api/extranet/dto/Favori'
import type { IFavoriDialog } from './IFavoriDialog'
import { useFavoriDialogStore } from './favoriDialogStore'
import { useFavorisApi } from '@/features/services/favoris/FavorisServices'
import { useAppStateStore } from '@/features/application'
import { injectLoadRechercheService } from '../recherches/rechercheRoutingServiceProvider'
import router from '@/router'

export const useFavorisStore = defineStore('favoris', () => {
  const storeDialog: IFavoriDialog = useFavoriDialogStore()
  const appState = useAppStateStore()

  const favorisApi = useFavorisApi<FavorisDTO[]>("quick-access")

  const canLoad = computed(() => {
    return appState.estUtilisateurAuthentifie && appState.estAuthentificationEnCours === false
  })

  const favoris = shallowRef<FavorisDTO[] | null>(null)

  async function loadFavoris() {
    if (canLoad.value) {
      favoris.value = (await favorisApi.loadFavoris()) ?? []
    } else {
      favoris.value = null
    }
  }

  watch(canLoad, loadFavoris)

  async function deleteFavori(key: string) {
    const newFavoris = favoris.value?.filter((item) => item.key !== key) ?? []

    try {
      await favorisApi.saveFavoris(newFavoris)
    } catch {
      return
    }

    favoris.value = newFavoris
  }

  async function addFavori(type: TypeRecherche, request: PagedRequest<object>) {
    const name = await storeDialog.showDialog(type)
    const newRequest: PagedRequest<object> = {
      ...request,
      pagination: { pageNumber: 1, pageSize: request.pagination.pageSize },
    }
    // console.log(router.currentRoute.value.path)
    const item: FavorisDTO = {
      key: new Date().toISOString(),
      name,
      type,
      request: {
        ...newRequest,
        route: {
          name: router.currentRoute.value.name,
          params: router.currentRoute.value.params,
        },
      },
    }
    const newFavoris = [...favoris.value!, item]
    try {
      await favorisApi.saveFavoris(newFavoris)
      // Maj state
      favoris.value = newFavoris
    } catch {
      // Rien à faire
    }
  }

  function navigateToFavori(key: string) {
    // Obtient le favori
    const favoriSelectionnné = favoris.value?.filter((item) => item.key === key).at(0)
    if (!favoriSelectionnné) return

    // Obtient le helper de navigation selon le type. A refactoriser
    const { loadPageWithPagedRequest } = injectLoadRechercheService(favoriSelectionnné.type)
    // Ouvre la page via le Helper
    loadPageWithPagedRequest(favoriSelectionnné.request)
  }

  return {
    favoris,
    loadFavoris,
    deleteFavori,
    addFavori,
    navigateToFavori,
  }
})

